/**
 * useSubscriptionStatus Hook
 * Checks if current user has a subscription
 */

import { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config/api';

interface UseSubscriptionStatusResult {
  status: string | null;
  hasAccess: boolean;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useSubscriptionStatus = (): UseSubscriptionStatusResult => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [hasAccess, setHasAccess] = useState(false);

  const fetchSubscriptionStatus = async () => {
    const token = localStorage.getItem('account-token');

    if (!token) {
      setHasAccess(false);
      setStatus(null);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const subInfo = await fetch(`${API_BASE_URL}/api/user/subscription`, {
        headers: { Authorization: `Bearer ${token}` },
        cache: 'no-store',
      });

      // Handle 401 - token is invalid/expired
      if (subInfo.status === 401) {
        console.warn('[useSubscriptionStatus] 401 Unauthorized - clearing session and redirecting to login');
        localStorage.removeItem('account-token');
        localStorage.removeItem('account-refresh-token');
        localStorage.removeItem('estate-plan-session');
        window.location.href = '/login';
        return;
      }

      const data = await subInfo.json();

      if (data.success) {
        setStatus(data.status);
        setHasAccess(data.hasAccess ?? false);
      } else {
        setError(data.message || 'Failed to load subscription');
        setHasAccess(false);
        setStatus(null);
      }
    } catch (err) {
      console.error('Subscription status check failed:', err);
      setError('Failed to verify subscription status');
      setHasAccess(false);
      setStatus(null);
    } finally {
      console.log('[useSubscriptionStatus] Fetching subscription status completed');
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscriptionStatus();
  }, []);

  const refetch = async () => {
    await fetchSubscriptionStatus();
  };

  return { status, hasAccess, isLoading, error, refetch };
};

export default useSubscriptionStatus;